import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function RestaurantVotingAppPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Restaurant Voting App — Vote on Where to Eat With Your Group"
        description="Democratic restaurant voting for groups, couples, and teams. Everyone swipes independently, Hangrily finds the restaurants you all agree on. No more 'I don't care, you pick' arguments."
        path="/restaurant-voting-app"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Restaurant Voting App — Democratic Food Decisions',
            description: 'Vote on where to eat with your group using a swipe-based restaurant picker. Everyone gets a vote. The algorithm finds consensus.',
            url: 'https://hangrily.app/restaurant-voting-app'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Restaurant Voting App', item: 'https://hangrily.app/restaurant-voting-app' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is there an app for voting on restaurants?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Hangrily is a restaurant voting app where everyone in your group swipes through restaurant cards independently. The algorithm only shows restaurants that received positive votes from everyone. It eliminates arguments and finds genuine group consensus in under 2 minutes.'
                }
              },
              {
                '@type': 'Question',
                name: 'How does restaurant voting work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Each person joins the session with a 6-digit code and swipes through the same restaurant cards. Swipe right to vote yes, left to vote no, up to vote love. Once everyone finishes, the algorithm calculates which restaurants got the most positive votes and advances the top picks to the next round. After up to 3 rounds, you have a winner everyone agreed on.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can we vote on restaurants as a couple?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Absolutely. Couple Mode is one of Hangrily's most popular features. Both partners swipe independently, and you only match on restaurants you both liked. No more 'I don't care, you pick' loops. The app handles the negotiation so you don't have to."
                }
              },
              {
                '@type': 'Question',
                name: 'What if someone in the group is picky?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "The algorithm is designed for mixed preferences. A single dislike doesn't eliminate a restaurant, but multiple dislikes do reduce its score. The scoring system weights love votes (3 points) higher than likes (1 point) and penalizes dislikes (-2 points). This means consensus favorites rise to the top while polarizing options get filtered out."
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Restaurant Voting App</h1>
          <p className="hero-subtitle">
            Democracy for your dinner. Everyone votes. The algorithm decides. Zero arguments.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Why Group Food Decisions Need a Vote</h2>
              <p>
                Planning dinner with friends, family, or coworkers shouldn't require a meeting. But it often 
                does. Someone suggests Italian. Another person had pasta yesterday. A third person is 
                vegetarian and the Italian place has one option. <strong>45 minutes later,</strong> nobody 
                has eaten and everyone is annoyed.
              </p>
              <p>
                The problem isn't your group — it's the process. Open-ended discussions let the loudest voice 
                win. Text threads devolve into "I don't care, whatever you want." Random wheels ignore 
                dietary needs and budgets. What you need is a <strong>blind voting system</strong> where 
                everyone gets equal say, and a smart algorithm finds the actual consensus.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Start a Group Vote →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">3-5x</span>
                  <span className="stat-label-sm">Longer group decisions take vs. solo</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">10</span>
                  <span className="stat-label-sm">Max group size per vote session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">100%</span>
                  <span className="stat-label-sm">Anonymous voting — no social pressure</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">Average time to reach group consensus</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">How Restaurant Voting Works</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Create a Voting Session</h3>
                <p>
                  One person starts a group session and gets a 6-digit code. Set the cuisine preferences, 
                  price range, and distance — so everyone only votes on restaurants that actually work for 
                  the group. No more "that place is too expensive" surprises.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Everyone Swipes Independently</h3>
                <p>
                  Each person joins with the code and swipes through the same restaurant cards on their 
                  own phone. Swipe right for yes, left for no, up for love. <strong>Nobody sees anyone 
                  else's votes</strong> until everyone is done. This prevents anchoring bias and social 
                  pressure — you vote your honest opinion.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>The Algorithm Finds Consensus</h3>
                <p>
                  Once all votes are in, the algorithm calculates scores: love = 3 points, like = 1 point, 
                  dislike = -2 points. It also applies a consensus bonus when everyone agrees, and a 
                  disagreement penalty when people are split. The top-scoring restaurants advance to the next 
                  round. Up to 3 rounds later, you have a winner everyone voted for.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Voting Modes for Every Group Size</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Couple Voting</h3>
              <p>
                The most popular voting mode. Both partners swipe independently, and you only match on 
                restaurants you both liked. No more "I don't care, you pick" conversations. The algorithm 
                does the negotiating for you.
              </p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Learn about Couple Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Friend Group Voting</h3>
              <p>
                Planning a group dinner? Everyone joins with a code and votes on their own phone. The 
                algorithm handles conflicting preferences automatically. Works for 3-10 people. Perfect for 
                birthday dinners, reunions, or regular friend meetups.
              </p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🏢</span>
              <h3>Team Lunch Voting</h3>
              <p>
                Office lunch decisions are the worst. Half the team wants salad, half wants burgers, and 
                the project manager just wants something fast. Create a session, share the code in Slack, 
                and let democracy decide where the team eats.
              </p>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👨‍👩‍👧‍👦</span>
              <h3>Family Voting</h3>
              <p>
                Kids want pizza. Parents want something with vegetables. Grandma wants a quiet place. 
                Hangrily's voting system lets every family member participate, while the preference filters 
                ensure only suitable restaurants make the ballot.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">The Science Behind Blind Restaurant Voting</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🤫</div>
              <h3>No Anchoring Bias</h3>
              <p>
                In traditional group decisions, the first person to suggest a restaurant anchors everyone's 
                thinking. "How about Italian?" suddenly makes Italian the default, even if nobody was craving 
                it. Blind voting eliminates this — everyone forms their own opinion first.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⚖️</div>
              <h3>Equal Voice for Everyone</h3>
              <p>
                In group conversations, extroverts dominate and introverts get overruled. Text threads let 
                the most persistent person win. Hangrily gives every voter exactly the same weight. A shy 
                person's love vote counts just as much as anyone else's.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🧮</div>
              <h3>Math, Not Drama</h3>
              <p>
                The scoring algorithm handles conflicts objectively. If 3 people love a restaurant and 1 
                dislikes it, the love votes outweigh the dislike. If the group is split, that restaurant 
                gets eliminated. No hurt feelings. No arguments. Just math.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is there an app for voting on restaurants?</h3>
              <p>
                Yes. Hangrily is a restaurant voting app where everyone swipes through restaurant cards 
                independently. The algorithm calculates which restaurants got the most positive votes and 
                finds the group consensus. It works for couples, friend groups, families, and teams.
              </p>
            </div>
            <div className="faq-item">
              <h3>How does restaurant voting work?</h3>
              <p>
                Each person joins a session with a 6-digit code and swipes through the same restaurant cards. 
                Right swipe = yes, left = no, up = love. Once everyone finishes, the algorithm scores each 
                restaurant and advances the winners to the next round. After up to 3 rounds, you have a 
                group-approved winner.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can couples vote on where to eat?</h3>
              <p>
                Absolutely. Couple Mode is Hangrily's most popular feature. Both partners swipe independently 
                and you only match on restaurants you both liked. It eliminates the "where do you want to 
                eat?" loop entirely. The app handles the decision so you can focus on enjoying dinner.
              </p>
            </div>
            <div className="faq-item">
              <h3>What if someone in the group is picky?</h3>
              <p>
                The algorithm is designed for real groups with real differences. A single picky eater won't 
                eliminate popular options, but restaurants with broad dislike get filtered out naturally. 
                The scoring weights consensus heavily, so restaurants that please most of the group rise 
                to the top while divisive options fall away.
              </p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Let Democracy Decide Dinner</h2>
          <p>Create a voting session, share the code, and let everyone have a say. Consensus in 2 minutes.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Start a Restaurant Vote →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
